/**
 * toTu.js — Convertit des textes rédigés à la 3e personne (« L'élève fait X »)
 * en tutoiement pédagogique (« Tu fais X »).
 *
 * Principe : conservateur. Mieux vaut ne pas transformer que mal
 * transformer.
 *
 * Pipeline :
 *   1) Remplacer le SUJET (Le motard / Le conducteur / L'élève / …) par « Tu »
 *   2) Convertir les pronoms réfléchis se/s' → te/t' UNIQUEMENT dans les
 *      enchaînements clairs (Tu direct / après « , » / après « et »)
 *   3) Conjuguer les verbes irréguliers
 *      - "safe" (comprend, choisit, réagit, …) : globalement
 *      - "ambigus" (peut, veut, doit, sait, fait, met, tient, a, est, va, …) :
 *        uniquement quand ils sont clairement gouvernés par « Tu »
 *   4) Conjuguer les verbes réguliers en -e après « Tu » ou « t' »
 *   5) Adapter les possessifs son/sa/ses → ton/ta/tes sur une whitelist de
 *      noms « personnels » (moto, casque, regard, visibilité, …)
 */

// -----------------------------------------------------------------------
// 1. Verbes irréguliers 3e sing → 2e sing
// -----------------------------------------------------------------------
// "SAFE" = très peu probable d'avoir un autre sujet dans nos textes.
const VERBS_SAFE = {
  'comprend': 'comprends',
  'apprend': 'apprends',
  'reprend': 'reprends',
  'surprend': 'surprends',
  'entreprend': 'entreprends',
  'descend': 'descends',
  'rend': 'rends',
  'tend': 'tends',
  'défend': 'défends',
  'répond': 'réponds',
  'conduit': 'conduis',
  'produit': 'produis',
  'reproduit': 'reproduis',
  'reconduit': 'reconduis',
  'séduit': 'séduis',
  'déduit': 'déduis',
  'induit': 'induis',
  'construit': 'construis',
  'reconstruit': 'reconstruis',
  'détruit': 'détruis',
  'introduit': 'introduis',
  'traduit': 'traduis',
  'cuit': 'cuis',
  'nuit': 'nuis',
  'instruit': 'instruis',
  'fuit': 'fuis',
  'écrit': 'écris',
  'décrit': 'décris',
  'inscrit': 'inscris',
  'lit': 'lis',
  'relit': 'relis',
  'choisit': 'choisis',
  'réagit': 'réagis',
  'agit': 'agis',
  'finit': 'finis',
  'réfléchit': 'réfléchis',
  'ralentit': 'ralentis',
  'accomplit': 'accomplis',
  'franchit': 'franchis',
  'réussit': 'réussis',
  'saisit': 'saisis',
  'aboutit': 'aboutis',
  'obéit': 'obéis',
  'unit': 'unis',
  'réunit': 'réunis',
  'rejoint': 'rejoins',
  'atteint': 'atteins',
  'restreint': 'restreins',
  'connaît': 'connais',
  'reconnaît': 'reconnais',
  'apparaît': 'apparais',
  'disparaît': 'disparais',
  'refait': 'refais',
  'satisfait': 'satisfais',
  'remet': 'remets',
  'commet': 'commets',
  'permet': 'permets',
  'promet': 'promets',
  'admet': 'admets',
  'transmet': 'transmets',
  'soumet': 'soumets',
  'ressort': 'ressors',
  'repart': 'repars',
  'accourt': 'accours',
  'parcourt': 'parcours',
  'ressent': 'ressens',
  'suffit': 'suffis',
  'sourit': 'souris',
  'obtient': 'obtiens',
  'contient': 'contiens',
  'maintient': 'maintiens',
  'retient': 'retiens',
  'soutient': 'soutiens',
  'appartient': 'appartiens',
};

// "AMBIGUS" = peuvent facilement se référer à un autre sujet (la moto peut,
// la roue tient, …). Ne remplacer que quand « Tu » gouverne clairement.
const VERBS_AMBIGUOUS = {
  'peut': 'peux',
  'veut': 'veux',
  'doit': 'dois',
  'sait': 'sais',
  'fait': 'fais',
  'met': 'mets',
  'tient': 'tiens',
  'vient': 'viens',
  'devient': 'deviens',
  'revient': 'reviens',
  'part': 'pars',
  'sort': 'sors',
  'sent': 'sens',
  'sert': 'sers',
  'court': 'cours',
  'dort': 'dors',
  'vit': 'vis',
  'revit': 'revis',
  'prend': 'prends',
  'attend': 'attends',
  'entend': 'entends',
  'vend': 'vends',
  'suit': 'suis',
  'a': 'as',
  'est': 'es',
  'va': 'vas',
};

// -----------------------------------------------------------------------
// 2. Remplacement du SUJET (3e pers → « Tu »)
// -----------------------------------------------------------------------
const SUBJECT_RULES = [
  // Début de phrase (majuscule)
  [/(^|[.!?…]\s+|\n\s*)(Le motard|Le conducteur|Le stagiaire|Le pilote|Le futur motard|L'\u00e9l\u00e8ve|L\u2019\u00e9l\u00e8ve)\s+/g, '$1Tu '],
  // Milieu de phrase (minuscule)
  [/\b(le motard|le conducteur|le stagiaire|le pilote|le futur motard|l'\u00e9l\u00e8ve|l\u2019\u00e9l\u00e8ve)\s+/g, 'tu '],

  // Compléments personnels
  [/\bde l['\u2019]\u00e9l\u00e8ve\b/g, 'de toi'],
  [/\bpar l['\u2019]\u00e9l\u00e8ve\b/g, 'par toi'],
  [/\bpour l['\u2019]\u00e9l\u00e8ve\b/g, 'pour toi'],
  [/\bavec l['\u2019]\u00e9l\u00e8ve\b/g, 'avec toi'],
];

// -----------------------------------------------------------------------
// 3. Pronoms réfléchis se/s' → te/t' UNIQUEMENT dans les enchaînements
//    clairs (directement après Tu / après « , » / après « et »)
// -----------------------------------------------------------------------
const REFLEXIVE_RULES = [
  // Directement après « Tu » (Le conducteur s'équipe → Tu s'équipe → Tu t'équipes)
  [/\bTu\s+s['\u2019]([aàâeéèêëiîïoôuùûyœæhAÀÂEÉÈÊËIÎÏOÔUÙÛYŒÆH])/g, "Tu t'$1"],
  [/\bTu\s+se\s/g, 'Tu te '],

  // Après « et » — même sujet implicite
  [/(\bTu\b[^.!?\n]{0,120}?)\bet\s+s['\u2019]([aàâeéèêëiîïoôuùûyœæh])/gi, "$1et t'$2"],
  [/(\bTu\b[^.!?\n]{0,120}?)\bet\s+se\s/gi, '$1et te '],

  // Après une virgule — enchaînement d'actions (Tu démarres, stabilises, et s'arrête)
  [/(\bTu\b[^.!?\n]{0,120}?),\s+s['\u2019]([aàâeéèêëiîïoôuùûyœæh])/gi, "$1, t'$2"],
  [/(\bTu\b[^.!?\n]{0,120}?),\s+se\s/gi, '$1, te '],
];

// -----------------------------------------------------------------------
// 4. Exclusions de conjugaison en -e
// -----------------------------------------------------------------------
const EXCLUS_DIRECT = new Set([
  // Adjectifs purs
  'stable', 'sensible', 'possible', 'utile', 'confortable', 'responsable',
  'agréable', 'large', 'double', 'simple', 'efficace', 'riche', 'mobile',
  'lente', 'précise', 'droite', 'gauche', 'plate', 'brute', 'lourde',
  'immobile', 'apte', 'proche', 'stricte', 'rapide', 'souple',
  // Noms purs (jamais verbes conjugués dans nos textes)
  'moto', 'route', 'voie', 'joie', 'panne', 'faute', 'tête',
  'chose', 'côte', 'porte', 'phase',
  'autre', 'même',
]);

const EXCLUS_ENUM = new Set([
  ...EXCLUS_DIRECT,
  // Plus prudent en énumération — ces mots sont plus souvent des noms
  'analyse', 'passe', 'marche', 'cache', 'garde', 'regarde', 'mise',
  'force', 'note', 'cause', 'sorte', 'face', 'prise', 'reprise',
  'surprise', 'entreprise', 'demande', 'attente', 'perte', 'sortie',
  'entrée', 'arrivée', 'poussée',
  // Noms de la sphère moto (souvent en énumération)
  'trajectoire', 'trajectoires', 'sécurité', 'visibilité', 'anticipation',
  'accélération', 'décélération', 'coordination', 'concentration',
  'vigilance', 'respiration', 'position', 'cadence', 'allure',
  // Environnement / conditions
  'fatigue', 'fatigues', 'vent', 'pluie', 'neige', 'nuit', 'brume',
  'météo', 'chaleur', 'froid', 'humidité', 'adhérence', 'circulation',
  'visibilité', 'luminosité', 'obscurité', 'panne',
  // Autres noms courants
  'urgence', 'aisance', 'confiance', 'expérience', 'patience',
  'présence', 'absence', 'distance', 'distances', 'cadence',
  'relance', 'tolérance', 'importance',
]);

// -----------------------------------------------------------------------
// 5. Whitelist des noms « personnels » pour son/sa/ses → ton/ta/tes
// -----------------------------------------------------------------------
const NOMS_PERSONNELS = [
  // Éléments moto
  'moto', 'machine', 'guidon', 'selle', 'casque', 'blouson', 'gants',
  'bottes', 'chaussures', 'équipement', 'protection', 'dorsale',
  // Corps
  'regard', 'tête', 'corps', 'buste', 'épaules', 'bras', 'coudes',
  'mains', 'main', 'jambes', 'genoux', 'pieds', 'pied', 'orteils',
  'nuque', 'colonne', 'poignée', 'poignées',
  // Actions personnelles
  'allure', 'vitesse', 'trajectoire', 'trajectoires', 'position',
  'positions', 'placement', 'placements', 'attitude', 'attitudes',
  'comportement', 'comportements', 'conduite', 'pilotage',
  'freinage', 'freinages', 'accélération', 'accélérations',
  'dosage', 'dosages', 'contrôle', 'contrôles',
  // Vérifications / gestes de sécurité
  'vérification', 'vérifications', 'contrôle', 'contrôles',
  'réflexes', 'réflexe', 'gestes', 'geste', 'réactions', 'réaction',
  // Notions pédagogiques
  'progression', 'progressions', 'formation', 'formations',
  'apprentissage', 'apprentissages', 'expérience', 'expériences',
  'vécu', 'ressenti', 'ressentis', 'niveau', 'niveaux',
  'point', 'points', 'axe', 'axes', 'objectif', 'objectifs',
  'plan', 'plans', 'stratégie', 'stratégies',
  // Environnement immédiat
  'moniteur', 'formateur', 'passager', 'passagère',
  // Sécurité personnelle
  'sécurité', 'visibilité', 'marge', 'marges', 'distance', 'distances',
  'espace', 'espaces', 'anticipation', 'anticipations',
  'concentration', 'attention', 'vigilance', 'lucidité',
  // Divers personnel
  'itinéraire', 'itinéraires', 'trajet', 'trajets', 'parcours',
  'temps', 'rythme', 'rythmes', 'style',
  // Sensations & auto-évaluation personnelles
  'sensation', 'sensations', 'perception', 'perceptions',
  'gestion', 'gestions', 'capacité', 'capacités',
  'prise', 'prises', 'condition', 'conditions',
  'équilibre', 'équilibres', 'repère', 'repères',
  'prestation', 'prestations', 'observation', 'observations',
  'décision', 'décisions', 'analyse', 'analyses',
  'auto-évaluation', 'évaluation', 'évaluations',
  'priorité', 'priorités', 'droit', 'droits',
];
const NOMS_PERSONNELS_RE = new RegExp(`^(?:${NOMS_PERSONNELS.join('|')})$`, 'i');

// -----------------------------------------------------------------------
// 6. Utils
// -----------------------------------------------------------------------
function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Applique la conjugaison des verbes "ambigus" UNIQUEMENT quand ils sont
 * clairement gouvernés par « Tu » : ils suivent « Tu » (directement ou
 * après une conjonction/virgule sans autre sujet nominal interposé).
 *
 * Cas spécial : après « et », on considère que le sujet est identique
 * à celui du groupe précédent (donc « Tu » implicite).
 */
function conjugateAmbiguous(text) {
  const verbsPattern = Object.keys(VERBS_AMBIGUOUS).join('|');
  const re = new RegExp(`\\b(${verbsPattern})\\b`, 'gi');
  return text.replace(re, (match, verb, offset) => {
    const back = text.slice(Math.max(0, offset - 100), offset);
    // Cas 1 — verbe précédé de "et " (continuité de sujet)
    if (/\bet\s+$/.test(back)) {
      const backBeforeEt = back.replace(/\bet\s+$/, '');
      if (/\bTu\b[^.!?\n]*$/.test(backBeforeEt)) {
        const good = VERBS_AMBIGUOUS[verb.toLowerCase()];
        return /^[A-Z]/.test(verb) ? capitalize(good) : good;
      }
    }
    // Cas 2 — chercher le dernier "candidat sujet" avant le verbe
    //   On IGNORE les formes possessives (ma/ta/sa/mon/ton/son + nom)
    //   qui sont plus souvent des compléments d'objet que des sujets.
    const candidates = [...back.matchAll(
      /\b(Tu|il|elle|on|la\s+[a-zàâçéèêëîïôûùüÿñæœ]+|le\s+[a-zàâçéèêëîïôûùüÿñæœ]+|les\s+[a-zàâçéèêëîïôûùüÿñæœ]+|un\s+[a-zàâçéèêëîïôûùüÿñæœ]+|une\s+[a-zàâçéèêëîïôûùüÿñæœ]+|cette?\s+[a-zàâçéèêëîïôûùüÿñæœ]+|ces\s+[a-zàâçéèêëîïôûùüÿñæœ]+)\b/gi
    )];
    if (candidates.length === 0) return match;
    const last = candidates[candidates.length - 1][0].trim().toLowerCase();
    if (last === 'tu') {
      const good = VERBS_AMBIGUOUS[verb.toLowerCase()];
      return /^[A-Z]/.test(verb) ? capitalize(good) : good;
    }
    return match;
  });
}

// -----------------------------------------------------------------------
// 7. POST-RULES appliquées après SUBJECT_RULES + REFLEXIVE_RULES
// -----------------------------------------------------------------------
const POST_RULES = [
  // A) Verbes irréguliers "safe" → globalement
  ...Object.keys(VERBS_SAFE).map(bad => [
    new RegExp(`\\b${bad}\\b`, 'g'),
    (m) => (/^[A-Z]/.test(m) ? capitalize(VERBS_SAFE[bad]) : VERBS_SAFE[bad]),
  ]),

  // B) Verbes réguliers -e après « Tu » directement
  [/\bTu\s+([a-zàâçéèêëîïôûùüÿñæœ]{2,})e\b/gi, (m, verb) => {
    const w = (verb + 'e').toLowerCase();
    if (EXCLUS_DIRECT.has(w)) return `Tu ${verb}e`;
    return `Tu ${verb}es`;
  }],

  // C) Verbes réguliers -e après « t' » (t'arrête → t'arrêtes)
  [/\bt['\u2019]([a-zàâçéèêëîïôûùüÿñæœ]{2,})e\b/g, "t'$1es"],

  // D) Verbes réguliers -e après « te » (te place → te places)
  [/\bte\s+([a-zàâçéèêëîïôûùüÿñæœ]{2,})e\b/g, (m, verb) => {
    const w = (verb + 'e').toLowerCase();
    if (EXCLUS_DIRECT.has(w)) return `te ${verb}e`;
    return `te ${verb}es`;
  }],

  // E) Verbes réguliers -e en énumération après virgule ou "et"
  //    Appliqué en BOUCLE tant qu'il reste des matches (pour couvrir
  //    plusieurs verbes en cascade : « Tu anticipes, informes, contrôles et exécutes »).
  //    Exclusion des participes féminins en -ée / -ue (« détendue », « coordonnée »)
  //    via un negative lookbehind sur le "e" final.
  [/(\bTu\b[^.!?\n]*?)(,\s+|\s+et\s+)([a-zàâçéèêëîïôûùüÿñæœ]{2,}[a-zàâçèêëîïôûùüÿñ])(?<![uéê])e(?=[,.\s!?])/gi, (m, before, sep, verb) => {
    const w = (verb + 'e').toLowerCase();
    if (EXCLUS_ENUM.has(w)) return `${before}${sep}${verb}e`;
    return `${before}${sep}${verb}es`;
  }],

  // E-bis) Whitelist verbes -uer (rares mais légitimes, exclus par la règle E)
  //   « et effectue → et effectues », « Tu évalue → Tu évalues », …
  [/(\bTu\b[^.!?\n]*?)(,\s+|\s+et\s+)(effectue|attribue|continue|évalue|situe|secoue|remue|accentue|substitue|contribue|distribue|évacue|diminue|atténue|salue|éternue|discontinue|redistribue)\b(?!s)/gi, '$1$2$3s'],
  [/\bTu\s+(effectue|attribue|continue|évalue|situe|secoue|remue|accentue|substitue|contribue|distribue|évacue|diminue|atténue|salue|éternue|discontinue|redistribue)\b(?!s)/gi, 'Tu $1s'],

  // F) Futur : « Tu identifiera » → « Tu identifieras »
  [/\bTu\s+([a-zàâçéèêëîïôûùüÿñæœ]+)ra\b/gi, 'Tu $1ras'],

  // G) Possessifs son/sa/ses → ton/ta/tes UNIQUEMENT si nom « personnel »
  [/\bson\s+([a-zàâçéèêëîïôûùüÿñæœ\-']+)/gi, (m, noun) => {
    if (NOMS_PERSONNELS_RE.test(noun)) return `ton ${noun}`;
    return m;
  }],
  [/\bsa\s+([a-zàâçéèêëîïôûùüÿñæœ\-']+)/gi, (m, noun) => {
    if (NOMS_PERSONNELS_RE.test(noun)) return `ta ${noun}`;
    return m;
  }],
  [/\bses\s+([a-zàâçéèêëîïôûùüÿñæœ\-']+)/gi, (m, noun) => {
    if (NOMS_PERSONNELS_RE.test(noun)) return `tes ${noun}`;
    return m;
  }],

  // H) Cas spécial « son moniteur / son formateur / son passager » — toujours personnel
  [/\bson (moniteur|formateur|prof|instructeur|passager)\b/g, 'ton $1'],
  [/\bsa (passagère|monitrice|formatrice)\b/g, 'ta $1'],

  // I) Filet de sécurité
  [/\bTu\s+ses\s+/g, 'Tu te '],
  [/\bTu\s+se\s+/g, 'Tu te '],
  [/\bTu\s+s['\u2019]/g, "Tu t'"],
];

function apply(rules, text) {
  let out = text;
  rules.forEach(([pattern, replacement]) => {
    out = out.replace(pattern, replacement);
  });
  return out;
}

function applyLoop(rules, text, maxPasses = 4) {
  let out = text;
  for (let i = 0; i < maxPasses; i++) {
    const next = apply(rules, out);
    if (next === out) break;
    out = next;
  }
  return out;
}

export function toTu(text) {
  if (!text || typeof text !== 'string') return text;
  const s1 = apply(SUBJECT_RULES, text);
  const s2 = apply(REFLEXIVE_RULES, s1);
  // On boucle POST_RULES car la règle d'énumération peut nécessiter
  // plusieurs passes pour couvrir « Tu X, Y, Z et W » en cascade.
  const s3 = applyLoop(POST_RULES, s2);
  // Étape ambigüe : conjuguer peut/veut/… seulement si Tu est le sujet local
  const s4 = conjugateAmbiguous(s3);
  return s4;
}

export function toTuArr(arr) {
  if (!Array.isArray(arr)) return arr;
  return arr.map(toTu);
}
